/*
 * Copyright (C) 2020 to Present Applause App Quality, Inc. All rights reserved.
 */

import { computed, Injectable, signal, Signal } from '@angular/core';
import { Observable, ReplaySubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { Option } from '../../option/option';
import { OptionSelectionService } from '../option-selection.service';
import { SelectionEvent } from '../selection-event';

@Injectable()
export class OptionSingleSelectionService extends OptionSelectionService {
  private readonly state = signal<SelectionEvent>({value: null, userInput: false});
  // Emissions are pushed directly from select()/clear() rather than derived via
  // toObservable(state), since that bridge relies on Angular's effect scheduler
  // and was found to occasionally never flush a notification to subscribers.
  private readonly selectionSubject = new ReplaySubject<SelectionEvent>(1);

  readonly selection: Signal<SelectionEvent> = this.state.asReadonly();
  readonly selection$: Observable<SelectionEvent> = this.selectionSubject.asObservable();

  constructor() {
    super();
    this.selectionSubject.next(this.state());
  }

  getSelection(): SelectionEvent {
    return this.state();
  }

  select(event: SelectionEvent): void {
    const alreadySelected = super.compareValues(event.value, this.state().value);
    if (alreadySelected) {
      return;
    }

    const next: SelectionEvent = {value: event.value, userInput: event.userInput};
    this.state.set(next);
    this.selectionSubject.next(next);
  }

  isSelected(option: Option): Signal<boolean> {
    return computed(() => super.compareValues(this.state().value, option.value));
  }

  isAnySelected(options: Option[]): Signal<boolean> {
    return computed(() => {
      const current = this.state().value;
      return options.some(option => super.compareValues(current, option.value));
    });
  }

  isSelected$(option: Option): Observable<boolean> {
    return this.selection$.pipe(
      map(event => super.compareValues(event.value, option.value)));
  }

  isAnySelected$(options: Option[]): Observable<boolean> {
    return this.selection$.pipe(
      map(event => options.some(option => super.compareValues(event.value, option.value))));
  }

  clear(event: { userInput: boolean }): void {
    const next: SelectionEvent = {value: null, userInput: event.userInput};
    this.state.set(next);
    this.selectionSubject.next(next);
  }

}
